// 管理员API服务

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

// 获取认证头（不带token）
const getBaseHeaders = () => {
  return {
    'Content-Type': 'application/json'
  }
}

// 获取认证头（带token）
const getAuthHeaders = () => {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  }
}

// 清除认证信息并跳转到登录页
const handleAuthExpired = () => {
  try {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    localStorage.setItem('isAuthenticated', 'false')
    localStorage.removeItem('userType')
  } catch { }
  if (typeof window !== 'undefined') {
    const p = window.location.pathname || ''
    if (!p.includes('/admin/login') && !p.endsWith('/login')) {
      window.location.href = '/admin/login?expired=true'
    }
  }
}

// 检查并刷新令牌（在操作过程中延续登录时间）
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null

const tryRefreshToken = async (): Promise<boolean> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  const refreshToken = localStorage.getItem('refreshToken')
  if (!refreshToken) {
    return false
  }

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data?.token) {
          localStorage.setItem('token', data.data.token)
          if (data.data.refreshToken) {
            localStorage.setItem('refreshToken', data.data.refreshToken)
          }
          console.log('令牌已自动刷新')
          return true
        }
      }
      return false
    } catch (error) {
      console.error('刷新令牌失败:', error)
      return false
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// 在成功请求后检查是否需要刷新令牌（基于请求频率）
let lastRefreshTime = 0
const REFRESH_INTERVAL = 30 * 60 * 1000 // 每30分钟检查一次是否需要刷新

const checkAndRefreshToken = async () => {
  const now = Date.now()
  if (now - lastRefreshTime > REFRESH_INTERVAL) {
    const refreshToken = localStorage.getItem('refreshToken')
    if (refreshToken) {
      lastRefreshTime = now
      // 异步刷新，不阻塞当前请求
      tryRefreshToken().catch(() => { })
    }
  }
}

// 通用请求函数（用于需要认证的请求）
const apiRequest = async (url: string, options: RequestInit = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      mode: 'cors',
      headers: {
        ...getAuthHeaders(),
        ...options.headers
      }
    })
    const contentType = response.headers.get('content-type') || ''
    const isJson = contentType.includes('application/json')
    const data = isJson ? await response.json() : await response.text()

    if (!response.ok) {
      const msg = isJson ? (data?.error?.message || data?.message) : String(data)

      // 认证失败处理
      if (response.status === 401 || response.status === 403 || /未提供有效的认证令牌|认证失败|unauthorized/i.test(String(msg))) {
        // 尝试刷新令牌
        const refreshed = await tryRefreshToken()
        if (refreshed) {
          // 刷新成功，重试原请求
          const retryResponse = await fetch(`${API_BASE_URL}${url}`, {
            ...options,
            mode: 'cors',
            headers: {
              ...getAuthHeaders(),
              ...options.headers
            }
          })
          if (retryResponse.ok) {
            const retryData = await retryResponse.json()
            return retryData
          }
        }
        // 刷新失败或重试失败，跳转到登录页
        handleAuthExpired()
      }
      throw new Error(msg || `HTTP error! status: ${response.status}`)
    }

    // 请求成功，检查是否需要刷新令牌（延续登录时间）
    checkAndRefreshToken()

    return data
  } catch (error) {
    console.error('API请求失败:', error)
    throw error
  }
}

// 通用请求函数（用于无需认证的请求，如登录）
const apiRequestWithoutAuth = async (url: string, options: RequestInit = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      mode: 'cors',
      headers: {
        ...getBaseHeaders(),
        ...options.headers
      }
    })
    const contentType = response.headers.get('content-type') || ''
    const isJson = contentType.includes('application/json')
    const data = isJson ? await response.json() : await response.text()
    if (!response.ok) {
      const msg = isJson ? (data?.error?.message || data?.message) : String(data)
      throw new Error(msg || `HTTP error! status: ${response.status}`)
    }
    return data
  } catch (error) {
    console.error('API请求失败:', error)
    throw error
  }
}

// 认证相关API
export const authApi = {
  // 管理员登录
  login: (credentials: { email: string; password: string }) =>
    apiRequestWithoutAuth('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ ...credentials, user_type: 'admin' })
    }),

  // 管理员登出
  logout: () =>
    apiRequest('/api/v1/admin/logout', {
      method: 'POST'
    }),

  // 刷新token
  refreshToken: (refreshToken: string) =>
    apiRequestWithoutAuth('/api/v1/admin/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken })
    }),

  // 获取当前管理员信息
  getCurrentAdmin: () =>
    apiRequest('/api/v1/admin/me'),

  // 修改密码
  changePassword: (data: { oldPassword: string; newPassword: string }) =>
    apiRequest('/api/v1/admin/change-password', {
      method: 'POST',
      body: JSON.stringify(data)
    })
}


// 仪表板相关API
export const dashboardApi = {
  // 获取统计数据
  getStats: () => apiRequest('/api/v1/admin/dashboard/stats'),
}

// 用户管理API
export const usersApi = {
  // 获取用户列表
  getUsers: (params?: {
    search?: string
    type?: string
    status?: string
    page?: number
    limit?: number
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.search) queryParams.append('search', params.search)
    if (params?.type) queryParams.append('user_type', params.type)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by)
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order)

    return apiRequest(`/api/v1/admin/users?${queryParams}`)
  },

  // 获取用户详情
  getUserById: (id: string) => apiRequest(`/api/v1/admin/users/${id}`),

  // 更新用户状态
  updateUserStatus: (id: string, status: string) =>
    apiRequest(`/api/v1/admin/users/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    }),
  createUser: (data: { email?: string; phone?: string; full_name: string; password?: string; user_type: 'student' | 'teacher' | 'admin' | 'referrer' }) =>
    apiRequest(`/api/v1/admin/users`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
  deleteUser: (id: string) =>
    apiRequest(`/api/v1/admin/users/${id}`, { method: 'DELETE' }),

  // 更新用户管理员备注（仅管理端可见）
  updateAdminNotes: (id: string, admin_notes: string) =>
    apiRequest(`/api/v1/admin/users/${id}/admin-notes`, {
      method: 'PATCH',
      body: JSON.stringify({ admin_notes })
    }),

  // 导出所有用户数据为CSV
  exportUsers: () =>
    apiRequest('/api/v1/admin/users/export/csv'),

  // 导出用户资源文件（头像、视频、图片等）
  exportUserResources: () =>
    apiRequest('/api/v1/admin/users/export/resources')
}

// 推广管理API
export const referralAdminApi = {
  setFreezeDays: (referrerId: number, freeze_days: number) =>
    apiRequest(`/api/v1/referral/admin/referrers/${referrerId}/freeze-days`, {
      method: 'POST',
      body: JSON.stringify({ freeze_days })
    }),

  runSettlement: () =>
    apiRequest('/api/v1/referral/admin/settlement/run', { method: 'POST' })
}

// 软件下载管理API
export const softwareAdminApi = {
  list: (platform?: string, latest?: boolean) => {
    const qs = new URLSearchParams()
    if (platform) qs.append('platform', platform)
    if (latest) qs.append('latest', 'true')
    const q = qs.toString()
    return apiRequest(`/api/v1/software/list${q ? `?${q}` : ''}`)
  },

  upload: (data: { software_name: string; version: string; platform: string; download_url: string; file_size?: number; file_md5?: string; release_notes?: string; is_latest?: boolean; is_required?: boolean }) =>
    apiRequest('/api/v1/software/admin/upload', { method: 'POST', body: JSON.stringify(data) })
}

// 教师申请API
export const teacherApplicationsApi = {
  // 获取教师申请列表
  getApplications: (params?: {
    page?: number
    limit?: number
    status?: string
    search?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.search) queryParams.append('search', params.search)
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by)
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order)

    const queryString = queryParams.toString()
    return apiRequest(`/api/v1/admin/teachers/applications${queryString ? '?' + queryString : ''}`)
  },

  // 获取教师申请详情
  getApplicationDetail: (id: string) =>
    apiRequest(`/api/v1/admin/teachers/applications/${id}`),

  // 批准教师申请
  approveApplication: (id: string, data?: {
    teacher_level?: number
    admin_notes?: string
    set_hourly_rate?: number
    categories?: string[]
  }) =>
    apiRequest(`/api/v1/admin/teachers/applications/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify(data || {})
    }),

  // 拒绝教师申请
  rejectApplication: (id: string, data: {
    rejection_reason: string
    admin_notes?: string
    allow_reapply?: boolean
  }) =>
    apiRequest(`/api/v1/admin/teachers/applications/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // 更新教师状态
  updateTeacherStatus: (id: string, data: {
    status: 'active' | 'suspended' | 'banned'
    reason: string
  }) =>
    apiRequest(`/api/v1/admin/teachers/applications/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    }),

  // 获取教师统计数据
  getTeacherStats: () =>
    apiRequest('/api/v1/admin/teachers/stats'),

  // 处理教师申请（保持向后兼容）
  updateApplicationStatus: (id: string, status: string, rejectionReason?: string) => {
    if (status === 'approved') {
      return apiRequest(`/api/v1/admin/teachers/applications/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({})
      })
    } else if (status === 'rejected') {
      return apiRequest(`/api/v1/admin/teachers/applications/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({
          rejection_reason: rejectionReason || '不符合要求'
        })
      })
    }
    return Promise.reject(new Error('Invalid status'))
  },

  // 管理员操作日志
  getTeacherLogs: (id: string, limit: number = 50) =>
    apiRequest(`/api/v1/admin/teachers/${id}/logs?limit=${limit}`),
  getAdminLogs: (params?: { operation?: string; url?: string; admin_email?: string; start_date?: string; end_date?: string; page?: number; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params?.operation) qs.append('operation', params.operation)
    if (params?.url) qs.append('url', params.url)
    if (params?.admin_email) qs.append('admin_email', params.admin_email)
    if (params?.start_date) qs.append('start_date', params.start_date)
    if (params?.end_date) qs.append('end_date', params.end_date)
    qs.append('page', String(params?.page || 1))
    qs.append('limit', String(params?.limit || 50))
    return apiRequest(`/api/v1/admin/logs?${qs.toString()}`)
  },

  // 更新教师费率（课时费和学生收费）
  updateTeacherRates: (id: string, data: {
    hourly_rate?: number
    student_rate_per_25min?: number
  }) =>
    apiRequest(`/api/v1/admin/teachers/applications/${id}/rates`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    }),

  // 更新教师业务分类
  updateTeacherCategories: (id: string, categories: string[]) =>
    apiRequest(`/api/v1/admin/teachers/applications/${id}/categories`, {
      method: 'PATCH',
      body: JSON.stringify({ categories })
    })
}

// 课程管理API
export const coursesApi = {
  // 获取课程列表
  getCourses: (params?: {
    page?: number
    limit?: number
    status?: string
    search?: string
    category?: string
    language?: string
    level?: string
    teacher_id?: number
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.search) queryParams.append('search', params.search)
    if (params?.category) queryParams.append('category', params.category)
    if (params?.language) queryParams.append('language', params.language)
    if (params?.level) queryParams.append('level', params.level)
    if (params?.teacher_id) queryParams.append('teacher_id', params.teacher_id.toString())
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by)
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order)

    const queryString = queryParams.toString()
    return apiRequest(`/api/v1/admin/courses${queryString ? '?' + queryString : ''}`)
  },

  // 获取课程详情
  getCourseDetail: (id: string) =>
    apiRequest(`/api/v1/admin/courses/${id}`),

  // 创建课程
  createCourse: (courseData: {
    title: string
    description: string
    duration_minutes: number
    max_students: number
    price_per_session: number
    tags?: string[]
    language: string
    level: 'beginner' | 'intermediate' | 'advanced'
    category?: string
    thumbnail_url?: string
    requirements?: string[]
    objectives?: string[]
    teacher_id: number
    status?: 'draft' | 'active' | 'inactive' | 'suspended'
  }) =>
    apiRequest('/api/v1/admin/courses', {
      method: 'POST',
      body: JSON.stringify(courseData)
    }),

  // 更新课程
  updateCourse: (id: string, courseData: Partial<{
    title: string
    description: string
    duration_minutes: number
    max_students: number
    price_per_session: number
    tags: string[]
    language: string
    level: 'beginner' | 'intermediate' | 'advanced'
    category: string
    thumbnail_url: string
    requirements: string[]
    objectives: string[]
    teacher_id: number
    status: 'draft' | 'active' | 'inactive' | 'suspended'
  }>) =>
    apiRequest(`/api/v1/admin/courses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(courseData)
    }),

  // 更新课程状态
  updateCourseStatus: (id: string, data: {
    status: 'draft' | 'active' | 'inactive' | 'suspended'
    reason?: string
  }) =>
    apiRequest(`/api/v1/admin/courses/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    }),

  // 删除课程
  deleteCourse: (id: string) =>
    apiRequest(`/api/v1/admin/courses/${id}`, {
      method: 'DELETE'
    }),

  // 获取课程统计数据
  getCourseStats: () =>
    apiRequest('/api/v1/admin/courses/stats'),

  // 保持向后兼容的API
  updateCourseStatusLegacy: (id: string, status: string) =>
    apiRequest(`/api/v1/admin/courses/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    }),
}

// 软件工具API
export const softwareApi = {
  // 获取软件列表
  list: () => apiRequest('/api/v1/software/list'),
  // 上传软件工具
  upload: (data: any) => apiRequest('/api/v1/software/admin/upload', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  // 删除软件工具
  delete: (id: number) => apiRequest(`/api/v1/software/admin/${id}`, {
    method: 'DELETE'
  }),
  // 更新软件工具
  update: (id: number, data: any) => apiRequest(`/api/v1/software/admin/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  })
}

// 课程分类API
export const categoriesApi = {
  // 获取分类列表
  getCategories: () => apiRequest('/api/v1/courses/categories'),

  // 创建分类
  createCategory: (categoryData: {
    name: string
    name_en?: string
    description?: string
    description_en?: string
  }) =>
    apiRequest('/api/v1/courses/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData)
    }),

  // 更新分类
  updateCategory: (id: string, categoryData: Partial<{
    name: string
    name_en?: string
    description?: string
    description_en?: string
  }>) =>
    apiRequest(`/api/v1/courses/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData)
    }),

  // 删除分类
  deleteCategory: (id: string) =>
    apiRequest(`/api/v1/courses/categories/${id}`, {
      method: 'DELETE'
    }),
}

// 预约管理API
export const bookingsApi = {
  getBookings: (params?: {
    page?: number
    limit?: number
    status?: string
    start_date?: string
    end_date?: string
    teacher_id?: number
    student_id?: number
    search?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.teacher_id) queryParams.append('teacher_id', params.teacher_id.toString())
    if (params?.student_id) queryParams.append('student_id', params.student_id.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by)
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order)

    const queryString = queryParams.toString()
    return apiRequest(`/api/v1/admin/bookings${queryString ? '?' + queryString : ''}`)
  },

  getBookingDetail: (id: number) =>
    apiRequest(`/api/v1/admin/bookings/${id}`)
}

// 支付管理API
export const paymentsApi = {
  // 获取支付列表
  getPayments: (params?: {
    page?: number
    limit?: number
    search?: string
    status?: string
    payment_method?: string
    type?: string
    start_date?: string
    end_date?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.payment_method) queryParams.append('payment_method', params.payment_method)
    if (params?.type) queryParams.append('type', params.type)
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by)
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order)

    return apiRequest(`/api/v1/admin/payments?${queryParams}`)
  },

  // 获取支付详情
  getPaymentDetail: (id: string) =>
    apiRequest(`/api/v1/admin/payments/${id}`),

  // 更新支付状态
  updatePaymentStatus: (id: string, status: string, reason?: string) =>
    apiRequest(`/api/v1/admin/payments/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reason })
    }),

  // 获取支付统计数据
  getPaymentStats: (params?: {
    start_date?: string
    end_date?: string
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)

    return apiRequest(`/api/v1/admin/payments/stats/overview?${queryParams}`)
  },

  // 导出支付数据
  exportPayments: (params?: {
    format?: 'csv' | 'json'
    start_date?: string
    end_date?: string
    status?: string
    payment_method?: string
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.format) queryParams.append('format', params.format)
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.payment_method) queryParams.append('payment_method', params.payment_method)

    return apiRequest(`/api/v1/admin/payments/export?${queryParams}`)
  },

  // 获取课程收益记录列表（学生收费、教师付费、利润）
  getEarningsRecords: (params?: {
    page?: number
    limit?: number
    search?: string
    status?: string
    start_date?: string
    end_date?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by)
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order)

    return apiRequest(`/api/v1/admin/payments/earnings?${queryParams}`)
  },

  // 获取收益统计汇总
  getEarningsStats: (params?: {
    start_date?: string
    end_date?: string
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)

    return apiRequest(`/api/v1/admin/payments/earnings/stats?${queryParams}`)
  },
}

// 数据分析API
export const analyticsApi = {
  // 获取综合分析概览
  getOverview: (period: '7d' | '30d' | '90d' | '1y' = '30d') =>
    apiRequest(`/api/v1/admin/analytics/overview?period=${period}`),

  // 获取用户增长分析
  getUserGrowth: (params?: {
    period?: '7d' | '30d' | '90d' | '1y'
    granularity?: 'hourly' | 'daily' | 'weekly' | 'monthly'
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.period) queryParams.append('period', params.period)
    if (params?.granularity) queryParams.append('granularity', params.granularity)

    return apiRequest(`/api/v1/admin/analytics/user-growth?${queryParams}`)
  },

  // 获取收入分析
  getRevenue: (params?: {
    period?: '7d' | '30d' | '90d' | '1y'
    granularity?: 'hourly' | 'daily' | 'weekly' | 'monthly'
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.period) queryParams.append('period', params.period)
    if (params?.granularity) queryParams.append('granularity', params.granularity)

    return apiRequest(`/api/v1/admin/analytics/revenue?${queryParams}`)
  },

  // 获取课程预约分析
  getBookings: (params?: {
    period?: '7d' | '30d' | '90d' | '1y'
    granularity?: 'hourly' | 'daily' | 'weekly' | 'monthly'
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.period) queryParams.append('period', params.period)
    if (params?.granularity) queryParams.append('granularity', params.granularity)

    return apiRequest(`/api/v1/admin/analytics/bookings?${queryParams}`)
  },

  // 获取教师绩效分析
  getTeacherPerformance: (params?: {
    period?: '7d' | '30d' | '90d' | '1y'
    limit?: number
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.period) queryParams.append('period', params.period)
    if (params?.limit) queryParams.append('limit', params.limit.toString())

    return apiRequest(`/api/v1/admin/analytics/teacher-performance?${queryParams}`)
  },

  // 生成自定义报表
  generateCustomReport: (data: {
    metrics: string[]
    dimensions: string[]
    filters?: Record<string, any>
    date_range?: {
      start_date?: string
      end_date?: string
    }
    group_by?: string[]
    order_by?: {
      field: string
      direction: 'asc' | 'desc'
    }
    limit?: number
  }) =>
    apiRequest('/api/v1/admin/analytics/custom-report', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
}

// 投诉处理API
export const complaintsApi = {
  // 获取投诉列表
  getComplaints: (status?: string) => {
    const queryParams = status ? `?status=${status}` : ''
    return apiRequest(`/api/v1/admin/complaints${queryParams}`)
  },

  // 获取投诉详情（包含费用信息）
  getComplaintDetail: (id: string) =>
    apiRequest(`/api/v1/admin/complaints/${id}`),

  // 更新投诉状态
  updateComplaintStatus: (id: string, status: string, resolution?: string, assignedTo?: string) =>
    apiRequest(`/api/v1/admin/complaints/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, resolution, assigned_to: assignedTo })
    }),

  // 处理投诉并执行财务操作
  resolveComplaint: (id: string, data: {
    resolution_type: 'teacher_fault' | 'student_fault'
    notes?: string
  }) =>
    apiRequest(`/api/v1/admin/complaints/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),
}

// 通知管理API
export const notificationsApi = {
  // 获取通知列表
  getNotifications: (status?: string) => {
    const queryParams = status ? `?status=${status}` : ''
    return apiRequest(`/api/v1/admin/notifications${queryParams}`)
  },

  // 创建通知
  createNotification: (notificationData: any) =>
    apiRequest('/api/v1/admin/notifications', {
      method: 'POST',
      body: JSON.stringify(notificationData)
    }),
}

// 内容管理API
export const contentApi = {
  // 静态页面管理
  staticPages: {
    // 获取所有静态页面
    getPages: (params?: {
      page?: number
      limit?: number
      search?: string
      status?: string
      type?: string
    }) => {
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.append('page', params.page.toString())
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.search) queryParams.append('search', params.search)
      if (params?.status) queryParams.append('status', params.status)
      if (params?.type) queryParams.append('type', params.type)

      return apiRequest(`/api/v1/admin/content/pages?${queryParams}`)
    },

    // 获取页面详情
    getPageById: (id: string) => apiRequest(`/api/v1/admin/content/pages/${id}`),

    // 创建页面
    createPage: (pageData: {
      title: string
      slug: string
      content: string
      excerpt?: string
      type: string
      status: 'draft' | 'published' | 'archived'
      meta_title?: string
      meta_description?: string
      meta_keywords?: string
      language?: string
      template?: string
      featured_image?: string
    }) =>
      apiRequest('/api/v1/admin/content/pages', {
        method: 'POST',
        body: JSON.stringify(pageData)
      }),

    // 更新页面
    updatePage: (id: string, pageData: Partial<{
      title: string
      slug: string
      content: string
      excerpt: string
      status: 'draft' | 'published' | 'archived'
      meta_title: string
      meta_description: string
      meta_keywords: string
      language: string
      template: string
      featured_image: string
    }>) =>
      apiRequest(`/api/v1/admin/content/pages/${id}`, {
        method: 'PUT',
        body: JSON.stringify(pageData)
      }),

    // 删除页面
    deletePage: (id: string) =>
      apiRequest(`/api/v1/admin/content/pages/${id}`, {
        method: 'DELETE'
      }),

    // 批量操作
    bulkUpdate: (data: {
      action: 'publish' | 'unpublish' | 'archive' | 'delete'
      pageIds: string[]
    }) =>
      apiRequest('/api/v1/admin/content/pages/bulk', {
        method: 'POST',
        body: JSON.stringify(data)
      }),

    // 获取页面版本历史
    getVersionHistory: (id: string) =>
      apiRequest(`/api/v1/admin/content/pages/${id}/versions`),

    // 恢复到特定版本
    restoreVersion: (id: string, versionId: string) =>
      apiRequest(`/api/v1/admin/content/pages/${id}/versions/${versionId}/restore`, {
        method: 'POST'
      })
  },

  // 博客/资讯管理
  articles: {
    // 获取文章列表
    getArticles: (params?: {
      page?: number
      limit?: number
      search?: string
      status?: string
      category?: string
      author?: string
      language?: string
      sort_by?: string
      sort_order?: 'asc' | 'desc'
    }) => {
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.append('page', params.page.toString())
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.search) queryParams.append('search', params.search)
      if (params?.status) queryParams.append('status', params.status)
      if (params?.category) queryParams.append('category', params.category)
      if (params?.author) queryParams.append('author', params.author)
      if (params?.language) queryParams.append('language', params.language)
      if (params?.sort_by) queryParams.append('sort_by', params.sort_by)
      if (params?.sort_order) queryParams.append('sort_order', params.sort_order)

      return apiRequest(`/api/v1/admin/content/articles?${queryParams}`)
    },

    // 获取文章详情
    getArticleById: (id: string) => apiRequest(`/api/v1/admin/content/articles/${id}`),

    // 创建文章
    createArticle: (articleData: {
      title: string
      slug: string
      content: string
      excerpt?: string
      status: 'draft' | 'published' | 'scheduled' | 'archived'
      published_at?: string
      author_id: string
      category_ids: string[]
      tags?: string[]
      language?: string
      featured_image?: string
      meta_title?: string
      meta_description?: string
      meta_keywords?: string
      allow_comments?: boolean
      is_featured?: boolean
      reading_time?: number
    }) =>
      apiRequest('/api/v1/admin/content/articles', {
        method: 'POST',
        body: JSON.stringify(articleData)
      }),

    // 更新文章
    updateArticle: (id: string, articleData: Partial<{
      title: string
      slug: string
      content: string
      excerpt: string
      status: 'draft' | 'published' | 'scheduled' | 'archived'
      published_at: string
      author_id: string
      category_ids: string[]
      tags: string[]
      language: string
      featured_image: string
      meta_title: string
      meta_description: string
      meta_keywords: string
      allow_comments: boolean
      is_featured: boolean
      reading_time: number
    }>) =>
      apiRequest(`/api/v1/admin/content/articles/${id}`, {
        method: 'PUT',
        body: JSON.stringify(articleData)
      }),

    // 删除文章
    deleteArticle: (id: string) =>
      apiRequest(`/api/v1/admin/content/articles/${id}`, {
        method: 'DELETE'
      }),

    // 批量操作
    bulkUpdate: (data: {
      action: 'publish' | 'unpublish' | 'archive' | 'delete' | 'feature' | 'unfeature'
      articleIds: string[]
    }) =>
      apiRequest('/api/v1/admin/content/articles/bulk', {
        method: 'POST',
        body: JSON.stringify(data)
      })
  },

  // 分类管理
  categories: {
    // 获取分类列表
    getCategories: (type?: string) => {
      const queryParams = type ? `?type=${type}` : ''
      return apiRequest(`/api/v1/admin/content/categories${queryParams}`)
    },

    // 创建分类
    createCategory: (categoryData: {
      name: string
      slug: string
      description?: string
      type: string
      parent_id?: string
      meta_title?: string
      meta_description?: string
      color?: string
      icon?: string
    }) =>
      apiRequest('/api/v1/admin/content/categories', {
        method: 'POST',
        body: JSON.stringify(categoryData)
      }),

    // 更新分类
    updateCategory: (id: string, categoryData: Partial<{
      name: string
      slug: string
      description: string
      parent_id: string
      meta_title: string
      meta_description: string
      color: string
      icon: string
    }>) =>
      apiRequest(`/api/v1/admin/content/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify(categoryData)
      }),

    // 删除分类
    deleteCategory: (id: string) =>
      apiRequest(`/api/v1/admin/content/categories/${id}`, {
        method: 'DELETE'
      })
  },

  // 标签管理
  tags: {
    // 获取标签列表
    getTags: (params?: {
      search?: string
      limit?: number
    }) => {
      const queryParams = new URLSearchParams()
      if (params?.search) queryParams.append('search', params.search)
      if (params?.limit) queryParams.append('limit', params.limit.toString())

      return apiRequest(`/api/v1/admin/content/tags?${queryParams}`)
    },

    // 创建标签
    createTag: (tagData: {
      name: string
      slug: string
      description?: string
      color?: string
    }) =>
      apiRequest('/api/v1/admin/content/tags', {
        method: 'POST',
        body: JSON.stringify(tagData)
      }),

    // 更新标签
    updateTag: (id: string, tagData: Partial<{
      name: string
      slug: string
      description: string
      color: string
    }>) =>
      apiRequest(`/api/v1/admin/content/tags/${id}`, {
        method: 'PUT',
        body: JSON.stringify(tagData)
      }),

    // 删除标签
    deleteTag: (id: string) =>
      apiRequest(`/api/v1/admin/content/tags/${id}`, {
        method: 'DELETE'
      })
  },

  // 媒体库管理
  media: {
    // 获取媒体文件列表
    getMedia: (params?: {
      page?: number
      limit?: number
      search?: string
      type?: string
      format?: string
      folder?: string
      sort_by?: string
      sort_order?: 'asc' | 'desc'
    }) => {
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.append('page', params.page.toString())
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.search) queryParams.append('search', params.search)
      if (params?.type) queryParams.append('type', params.type)
      if (params?.format) queryParams.append('format', params.format)
      if (params?.folder) queryParams.append('folder', params.folder)
      if (params?.sort_by) queryParams.append('sort_by', params.sort_by)
      if (params?.sort_order) queryParams.append('sort_order', params.sort_order)

      return apiRequest(`/api/v1/admin/content/media?${queryParams}`)
    },

    // 上传文件
    uploadFile: (file: File, metadata?: {
      folder?: string
      alt?: string
      caption?: string
      description?: string
      tags?: string[]
    }) => {
      const formData = new FormData()
      formData.append('file', file)
      if (metadata) {
        Object.entries(metadata).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            formData.append(key, JSON.stringify(value))
          } else if (value !== undefined) {
            formData.append(key, value.toString())
          }
        })
      }

      return apiRequest('/api/v1/admin/content/media/upload', {
        method: 'POST',
        body: formData,
        headers: {} // 让浏览器自动设置 Content-Type 为 multipart/form-data
      })
    },

    // 获取文件详情
    getMediaById: (id: string) => apiRequest(`/api/v1/admin/content/media/${id}`),

    // 更新文件信息
    updateMedia: (id: string, metadata: {
      alt?: string
      caption?: string
      description?: string
      tags?: string[]
      folder?: string
    }) =>
      apiRequest(`/api/v1/admin/content/media/${id}`, {
        method: 'PUT',
        body: JSON.stringify(metadata)
      }),

    // 删除文件
    deleteMedia: (id: string) =>
      apiRequest(`/api/v1/admin/content/media/${id}`, {
        method: 'DELETE'
      }),

    // 批量删除
    bulkDelete: (mediaIds: string[]) =>
      apiRequest('/api/v1/admin/content/media/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ mediaIds })
      }),

    // 获取文件夹列表
    getFolders: () => apiRequest('/api/v1/admin/content/media/folders'),

    // 创建文件夹
    createFolder: (folderData: {
      name: string
      parent_id?: string
    }) =>
      apiRequest('/api/v1/admin/content/media/folders', {
        method: 'POST',
        body: JSON.stringify(folderData)
      })
  },

  // SEO设置
  seo: {
    // 获取SEO设置
    getSettings: () => apiRequest('/api/v1/admin/content/seo/settings'),

    // 更新SEO设置
    updateSettings: (settings: {
      site_title?: string
      site_description?: string
      site_keywords?: string
      default_meta_title?: string
      default_meta_description?: string
      default_meta_keywords?: string
      robots_txt?: string
      sitemap_enabled?: boolean
      google_analytics_id?: string
      google_search_console?: string
      bing_webmaster_tools?: string
    }) =>
      apiRequest('/api/v1/admin/content/seo/settings', {
        method: 'PUT',
        body: JSON.stringify(settings)
      }),

    // 生成网站地图
    generateSitemap: () =>
      apiRequest('/api/v1/admin/content/seo/sitemap/generate', {
        method: 'POST'
      }),

    // 获取重定向规则
    getRedirects: (params?: {
      page?: number
      limit?: number
      search?: string
    }) => {
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.append('page', params.page.toString())
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.search) queryParams.append('search', params.search)

      return apiRequest(`/api/v1/admin/content/seo/redirects?${queryParams}`)
    },

    // 创建重定向规则
    createRedirect: (redirectData: {
      from_url: string
      to_url: string
      type: '301' | '302'
      description?: string
    }) =>
      apiRequest('/api/v1/admin/content/seo/redirects', {
        method: 'POST',
        body: JSON.stringify(redirectData)
      }),

    // 更新重定向规则
    updateRedirect: (id: string, redirectData: Partial<{
      to_url: string
      type: '301' | '302'
      description: string
    }>) =>
      apiRequest(`/api/v1/admin/content/seo/redirects/${id}`, {
        method: 'PUT',
        body: JSON.stringify(redirectData)
      }),

    // 删除重定向规则
    deleteRedirect: (id: string) =>
      apiRequest(`/api/v1/admin/content/seo/redirects/${id}`, {
        method: 'DELETE'
      })
  }
}

// 系统设置API
export const settingsApi = {
  // 获取系统设置
  getSettings: () => apiRequest('/api/v1/admin/settings'),

  // 更新系统设置
  updateSettings: (settings: Record<string, any>) =>
    apiRequest('/api/v1/admin/settings', {
      method: 'POST',
      body: JSON.stringify(settings)
    }),
}

// 教材管理API
export const materialsApi = {
  // 获取教材列表
  getMaterials: (params?: {
    page?: number
    limit?: number
    search?: string
    language?: string
    level?: string
    category?: string
    category_id?: number
    is_active?: boolean
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.language) queryParams.append('language', params.language)
    if (params?.level) queryParams.append('level', params.level)
    if (params?.category) queryParams.append('category', params.category)
    if (params?.category_id) queryParams.append('category_id', params.category_id.toString())
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active ? '1' : '0')
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by)
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order)

    return apiRequest(`/api/v1/admin/materials?${queryParams}`)
  },

  // 获取教材详情
  getMaterialById: (id: string) => apiRequest(`/api/v1/admin/materials/${id}`),

  // 创建教材
  createMaterial: (materialData: {
    material_name: string
    material_name_en?: string
    description?: string
    description_en?: string
    language: string
    level?: string
    category?: string
    category_id?: number
    relative_path?: string
    total_pages?: number
    file_url?: string
    file_size?: number
    cover_image_url?: string
    is_public?: boolean
    visibility?: 'all' | 'teacher' | 'admin'
  }) =>
    apiRequest('/api/v1/admin/materials', {
      method: 'POST',
      body: JSON.stringify(materialData)
    }),

  // 更新教材
  updateMaterial: (id: string, materialData: Partial<{
    material_name: string
    material_name_en: string
    description: string
    description_en: string
    language: string
    level: string
    category: string
    category_id: number
    total_pages: number
    file_url: string
    file_size: number
    cover_image_url: string
    is_public: boolean
    visibility: 'all' | 'teacher' | 'admin'
    is_active: boolean
  }>) =>
    apiRequest(`/api/v1/admin/materials/${id}`, {
      method: 'PUT',
      body: JSON.stringify(materialData)
    }),

  // 删除教材
  deleteMaterial: (id: string, force: boolean = false) =>
    apiRequest(`/api/v1/admin/materials/${id}${force ? '?force=true' : ''}`, {
      method: 'DELETE'
    }),

  // 检查教材使用情况
  checkMaterialUsage: (id: string) =>
    apiRequest(`/api/v1/admin/materials/${id}/usage`),

  // 获取教材统计
  getMaterialStats: () => apiRequest('/api/v1/admin/materials/stats'),

  // 上传教材文件
  uploadFile: async (file: File, type: 'material' | 'cover' = 'material') => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', type)

    const token = localStorage.getItem('token')
    const API_BASE_URL = import.meta.env.VITE_API_URL || ''

    const response = await fetch(`${API_BASE_URL}/api/v1/upload`, {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: formData
    })

    if (!response.ok) {
      throw new Error('文件上传失败')
    }

    return response.json()
  }
}

// 教材分类管理API
export const materialCategoriesApi = {
  // 获取分类列表（树形结构）
  getCategories: (flat?: boolean) =>
    apiRequest(`/api/v1/admin/categories${flat ? '?flat=1' : ''}`),

  // 获取单个分类详情
  getCategoryById: (id: number) =>
    apiRequest(`/api/v1/admin/categories/${id}`),

  // 获取分类下的教材
  getCategoryMaterials: (id: number, params?: {
    page?: number
    limit?: number
    include_children?: boolean
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.include_children) queryParams.append('include_children', '1')
    return apiRequest(`/api/v1/admin/categories/${id}/materials?${queryParams}`)
  },

  // 创建分类
  createCategory: (data: {
    name: string
    name_en?: string
    parent_id?: number
    sort_order?: number
    icon?: string
    description?: string
  }) =>
    apiRequest('/api/v1/admin/categories', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // 更新分类
  updateCategory: (id: number, data: Partial<{
    name: string
    name_en: string
    parent_id: number | null
    sort_order: number
    icon: string
    description: string
    is_active: boolean
  }>) =>
    apiRequest(`/api/v1/admin/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // 删除分类
  deleteCategory: (id: number, force?: boolean) =>
    apiRequest(`/api/v1/admin/categories/${id}${force ? '?force=1' : ''}`, {
      method: 'DELETE'
    }),

  // 批量调整分类顺序
  reorderCategories: (orders: Array<{ id: number; sort_order: number }>) =>
    apiRequest('/api/v1/admin/categories/reorder', {
      method: 'POST',
      body: JSON.stringify({ orders })
    })
}

// 教师提现管理API
export const teacherWithdrawalsApi = {
  // 获取提现申请列表
  getWithdrawals: (params?: {
    page?: number
    limit?: number
    status?: 'pending' | 'approved' | 'rejected' | 'completed'
    teacher_id?: number
    search?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.teacher_id) queryParams.append('teacher_id', params.teacher_id.toString())
    if (params?.search) queryParams.append('search', params.search)
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by)
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order)

    return apiRequest(`/api/v1/admin/teacher-withdrawals?${queryParams}`)
  },

  // 审批提现申请
  approveWithdrawal: (id: number, notes?: string) =>
    apiRequest(`/api/v1/admin/teacher-withdrawals/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ admin_notes: notes })
    }),

  // 拒绝提现申请
  rejectWithdrawal: (id: number, reason: string, notes?: string) =>
    apiRequest(`/api/v1/admin/teacher-withdrawals/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ rejection_reason: reason, admin_notes: notes })
    })
}

// 支付通道配置API
export const paymentChannelsApi = {
  // 获取所有支付通道
  getChannels: () => apiRequest('/api/v1/admin/payment-channels'),

  // 获取单个支付通道详情
  getChannelById: (id: number) => apiRequest(`/api/v1/admin/payment-channels/${id}`),

  // 创建支付通道
  createChannel: (data: {
    channel_code: string
    channel_name: string
    channel_type: string
    config: { pid: string; key: string }
    supported_methods: string[]
    api_url?: string
    is_active?: boolean
    is_default?: boolean
    priority?: number
    description?: string
  }) =>
    apiRequest('/api/v1/admin/payment-channels', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // 更新支付通道
  updateChannel: (id: number, data: {
    channel_name?: string
    config?: any  // 支持不同类型的配置
    supported_methods?: string[]
    api_url?: string
    is_active?: boolean
    is_default?: boolean
    priority?: number
    description?: string
    admin_notes?: string
  }) =>
    apiRequest(`/api/v1/admin/payment-channels/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // 启用/禁用支付通道
  toggleChannel: (id: number, is_active: boolean) =>
    apiRequest(`/api/v1/admin/payment-channels/${id}/toggle`, {
      method: 'POST',
      body: JSON.stringify({ is_active })
    }),

  // 设置默认支付通道
  setDefaultChannel: (id: number) =>
    apiRequest(`/api/v1/admin/payment-channels/${id}/set-default`, {
      method: 'POST'
    }),

  // 删除支付通道
  deleteChannel: (id: number) =>
    apiRequest(`/api/v1/admin/payment-channels/${id}`, {
      method: 'DELETE'
    }),

  // 获取支付通道操作日志
  getChannelLogs: (id: number, params?: { page?: number; limit?: number }) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    return apiRequest(`/api/v1/admin/payment-channels/${id}/logs?${queryParams}`)
  }
}

// 充值配置管理API
export const rechargeConfigApi = {
  // 获取预设金额列表
  getPresetAmounts: () => apiRequest('/api/v1/admin/recharge-config/preset-amounts'),

  // 创建预设金额
  createPresetAmount: (data: {
    amount: number
    display_order?: number
    is_active?: boolean
    is_popular?: boolean
  }) =>
    apiRequest('/api/v1/admin/recharge-config/preset-amounts', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // 更新预设金额
  updatePresetAmount: (id: number, data: {
    amount?: number
    display_order?: number
    is_active?: boolean
    is_popular?: boolean
  }) =>
    apiRequest(`/api/v1/admin/recharge-config/preset-amounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // 删除预设金额
  deletePresetAmount: (id: number) =>
    apiRequest(`/api/v1/admin/recharge-config/preset-amounts/${id}`, {
      method: 'DELETE'
    }),

  // 获取赠送规则列表
  getBonusRules: () => apiRequest('/api/v1/admin/recharge-config/bonus-rules'),

  // 创建赠送规则
  createBonusRule: (data: {
    min_amount: number
    max_amount?: number | null
    bonus_type: 'percentage' | 'fixed'
    bonus_value: number
    description?: string
    is_active?: boolean
    priority?: number
  }) =>
    apiRequest('/api/v1/admin/recharge-config/bonus-rules', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // 更新赠送规则
  updateBonusRule: (id: number, data: {
    min_amount?: number
    max_amount?: number | null
    bonus_type?: 'percentage' | 'fixed'
    bonus_value?: number
    description?: string
    is_active?: boolean
    priority?: number
  }) =>
    apiRequest(`/api/v1/admin/recharge-config/bonus-rules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // 删除赠送规则
  deleteBonusRule: (id: number) =>
    apiRequest(`/api/v1/admin/recharge-config/bonus-rules/${id}`, {
      method: 'DELETE'
    }),

  // 获取系统配置
  getSystemConfig: () => apiRequest('/api/v1/admin/recharge-config/system-config'),

  // 更新系统配置
  updateSystemConfig: (data: {
    min_recharge_amount?: number
    allow_custom_amount?: boolean
  }) =>
    apiRequest('/api/v1/admin/recharge-config/system-config', {
      method: 'PUT',
      body: JSON.stringify(data)
    })
}

// 提现手续费配置API
export const withdrawalFeeApi = {
  // 获取提现手续费配置
  getConfig: () => apiRequest('/api/v1/admin/withdrawal-fee-config'),

  // 更新提现手续费配置
  updateConfig: (data: {
    teacher_withdrawal_fee_rate?: number
    min_withdrawal_amount?: number
    max_withdrawal_amount?: number
  }) =>
    apiRequest('/api/v1/admin/withdrawal-fee-config', {
      method: 'PUT',
      body: JSON.stringify(data)
    })
}

// 会员等级管理 API
export const memberLevelsApi = {
  // 获取所有会员等级
  getAll: () => apiRequest('/api/v1/admin/member-levels'),

  // 创建会员等级
  create: (data: {
    name: string
    name_en?: string
    icon?: string
    color?: string
    sort_order?: number
    description?: string
  }) =>
    apiRequest('/api/v1/admin/member-levels', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // 更新会员等级
  update: (id: number, data: {
    name?: string
    name_en?: string
    icon?: string
    color?: string
    sort_order?: number
    description?: string
    is_active?: number
  }) =>
    apiRequest(`/api/v1/admin/member-levels/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // 删除会员等级
  delete: (id: number) =>
    apiRequest(`/api/v1/admin/member-levels/${id}`, {
      method: 'DELETE'
    }),

  // 设置学生会员等级
  assignToStudent: (data: {
    student_id: number
    level_id: number | null
  }) =>
    apiRequest('/api/v1/admin/member-levels/assign', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // 设置学生折扣
  setStudentDiscount: (data: {
    student_id: number
    discount_threshold: number
    discount_rate: number
  }) =>
    apiRequest('/api/v1/admin/member-levels/discount', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // 获取学生会员等级信息
  getStudentInfo: (studentId: number) =>
    apiRequest(`/api/v1/admin/member-levels/student/${studentId}`)
}

// 短信配置管理 API
export const smsConfigApi = {
  // 获取所有短信平台配置
  getAll: () => apiRequest('/api/v1/admin/sms-config'),

  // 获取单个配置详情
  getById: (id: number) => apiRequest(`/api/v1/admin/sms-config/${id}`),

  // 创建短信平台配置
  create: (data: {
    platform_code: string
    platform_name: string
    app_key?: string
    master_secret?: string
    dev_key?: string
    api_dev_secret?: string
    sign_id?: string
    template_id?: string
    api_url?: string
    rate_limit_per_ip?: number
    rate_limit_per_phone?: number
    code_expire_minutes?: number
    code_length?: number
  }) =>
    apiRequest('/api/v1/admin/sms-config', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // 更新短信平台配置
  update: (id: number, data: {
    platform_name?: string
    app_key?: string
    master_secret?: string
    dev_key?: string
    api_dev_secret?: string
    sign_id?: string
    template_id?: string
    api_url?: string
    rate_limit_per_ip?: number
    rate_limit_per_phone?: number
    code_expire_minutes?: number
    code_length?: number
  }) =>
    apiRequest(`/api/v1/admin/sms-config/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),

  // 启用短信平台
  activate: (id: number) =>
    apiRequest(`/api/v1/admin/sms-config/${id}/activate`, {
      method: 'POST'
    }),

  // 删除短信平台配置
  delete: (id: number) =>
    apiRequest(`/api/v1/admin/sms-config/${id}`, {
      method: 'DELETE'
    }),

  // 测试发送短信
  test: (data: { phone: string; config_id?: number }) =>
    apiRequest('/api/v1/admin/sms-config/test', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // 验证验证码（测试功能）
  verify: (data: { phone: string; code: string }) =>
    apiRequest('/api/v1/admin/sms-config/verify', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  // 查询短信余量
  getBalance: (configId?: number) => {
    const url = configId
      ? `/api/v1/admin/sms-config/balance?config_id=${configId}`
      : '/api/v1/admin/sms-config/balance'
    return apiRequest(url)
  },

  // 获取发送统计
  getStats: () => apiRequest('/api/v1/admin/sms-config/stats')
}
